import React, { useState } from "react";
import ItemPicker from "./ItemPicker";

/**
 * Manages the loadout list for a single scope inside an event form. Stateless
 * with respect to persistence — caller owns the items array and gets a new
 * array back via `onChange`. EventFormModal stores all scopes' arrays in form
 * state and saves them together when the event is saved.
 *
 * Each item in the array has shape:
 *   {
 *     id?:        existing row UUID, or
 *     temp_id?:   client-side ID for unsaved rows,
 *     item_id:    sc_items UUID,
 *     _item:      { id, name, image_url, category, subcategory, manufacturer, item_group },
 *     quantity:   number | null,
 *     note:       string,
 *     required:   boolean,
 *     sort_order: number
 *   }
 *
 * Props:
 *   items, onChange  — controlled list
 *   title            — section header (e.g. "Ship Weapons & Components")
 *   addLabel         — button text (e.g. "Add Weapon/Component")
 *   groupFilter      — picker filter (array of item_group names)
 *   categoryFilter   — picker filter (array of category names)
 *   accentColor      — section accent
 *   emptyHint        — shown when no items added yet
 *   compact          — tighter row styling for deeply-nested editors
 */
export default function EventItemsEditor({
  items = [],
  onChange,
  title,
  addLabel = "Add Item",
  groupFilter = null,
  categoryFilter = null,
  accentColor = "#fcd657",
  emptyHint = "No items yet.",
  compact = false,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  function emit(next) {
    onChange((next || []).map((r, i) => ({ ...r, sort_order: i })));
  }

  function handleAdd(item) {
    const next = [
      ...items,
      {
        temp_id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        item_id: item.id,
        _item: item,
        quantity: null,
        note: "",
        required: false,
        sort_order: items.length,
      },
    ];
    emit(next);
    setPickerOpen(false);
  }

  function patchRow(idx, patch) {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    emit(next);
  }

  function removeRow(idx) {
    const next = items.slice();
    next.splice(idx, 1);
    emit(next);
  }

  function moveRow(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    emit(next);
  }

  const rowPad = compact ? "6px 8px" : "10px 12px";
  const imgSize = compact ? 28 : 36;
  const titleFs = compact ? 12 : 13;
  const metaFs = compact ? 8 : 9;

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        {title && (
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: accentColor, textTransform: "uppercase" }}>
            {title} {items.length > 0 && <span style={{ color: "rgba(232,234,246,0.4)", marginLeft: 4 }}>({items.length})</span>}
          </div>
        )}
        <button type="button" onClick={() => setPickerOpen(true)} style={{
          fontFamily: "'Orbitron', sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase",
          padding: "5px 12px", background: `${accentColor}14`, border: `1px solid ${accentColor}88`, color: accentColor,
          cursor: "pointer", transition: "all 0.15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = `${accentColor}22`; }}
          onMouseLeave={e => { e.currentTarget.style.background = `${accentColor}14`; }}>
          + {addLabel}
        </button>
      </div>

      {/* Rows */}
      {items.length === 0 ? (
        <div style={{ padding: compact ? "8px 12px" : "12px 16px", border: "1px dashed rgba(255,255,255,0.08)", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1, color: "rgba(232,234,246,0.3)", textAlign: "center", textTransform: "uppercase" }}>
          {emptyHint}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {items.map((row, idx) => {
            const item = row._item || {};
            return (
              <div key={row.id || row.temp_id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: rowPad,
                background: row.required ? "rgba(239,83,80,0.06)" : "rgba(255,255,255,0.02)",
                border: `1px solid ${row.required ? "rgba(239,83,80,0.3)" : "rgba(255,255,255,0.05)"}`,
              }}>
                {/* Reorder */}
                <div style={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
                  <button type="button" onClick={() => moveRow(idx, -1)} disabled={idx === 0}
                    style={{ width: 18, height: 14, padding: 0, background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: idx === 0 ? "rgba(255,255,255,0.15)" : "rgba(232,234,246,0.45)", cursor: idx === 0 ? "default" : "pointer", fontSize: 8, lineHeight: 1 }}>▲</button>
                  <button type="button" onClick={() => moveRow(idx, +1)} disabled={idx === items.length - 1}
                    style={{ width: 18, height: 14, padding: 0, background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: idx === items.length - 1 ? "rgba(255,255,255,0.15)" : "rgba(232,234,246,0.45)", cursor: idx === items.length - 1 ? "default" : "pointer", fontSize: 8, lineHeight: 1 }}>▼</button>
                </div>

                {/* Thumb */}
                <div style={{ width: imgSize, height: imgSize, flexShrink: 0, overflow: "hidden", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 12, color: `${accentColor}25` }}>◇</div>
                  )}
                </div>

                {/* Name + meta */}
                <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: titleFs, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name || "(unknown item)"}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: metaFs, letterSpacing: 1, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.category}{item.subcategory ? ` · ${item.subcategory}` : ""}
                    {item.item_group === "Commodity" && row.quantity > 0 && row.container_size_scu > 0 && (
                      <span style={{ color: "#34d399", marginLeft: 6 }}>
                        · {row.quantity * row.container_size_scu} SCU total
                      </span>
                    )}
                  </div>
                </div>

                {/* Quantity — for commodities this is "# of containers", otherwise just a count. */}
                <input type="number" min="0" placeholder={item.item_group === "Commodity" ? "Boxes" : "Qty"} value={row.quantity ?? ""}
                  onChange={e => patchRow(idx, { quantity: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  title={item.item_group === "Commodity" ? "Number of SCU containers" : "Quantity"}
                  style={{ width: 56, padding: "5px 7px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#e8eaf6", fontFamily: "'Space Mono', monospace", fontSize: 11, textAlign: "center", borderRadius: 2 }} />

                {/* SCU container size — only for commodity rows */}
                {item.item_group === "Commodity" && (
                  <select value={row.container_size_scu ?? ""}
                    onChange={e => patchRow(idx, { container_size_scu: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                    title="SCU container size"
                    style={{ padding: "5px 7px", background: "#0a0d18", border: "1px solid rgba(52,211,153,0.4)", color: "#34d399", fontFamily: "'Space Mono', monospace", fontSize: 10, borderRadius: 2, flexShrink: 0 }}>
                    <option value="">SCU</option>
                    <option value="1">1 SCU</option>
                    <option value="2">2 SCU</option>
                    <option value="4">4 SCU</option>
                    <option value="8">8 SCU</option>
                    <option value="16">16 SCU</option>
                    <option value="24">24 SCU</option>
                    <option value="32">32 SCU</option>
                  </select>
                )}

                {/* Required toggle */}
                <button type="button" onClick={() => patchRow(idx, { required: !row.required })}
                  title={row.required ? "Required — click to mark recommended" : "Recommended — click to mark required"}
                  style={{
                    fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.2, textTransform: "uppercase",
                    padding: "4px 8px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                    background: row.required ? "rgba(239,83,80,0.18)" : "transparent",
                    border: `1px solid ${row.required ? "#ef5350" : "rgba(255,255,255,0.15)"}`,
                    color: row.required ? "#ef5350" : "rgba(232,234,246,0.5)",
                  }}>
                  {row.required ? "● Required" : "○ Recommended"}
                </button>

                {/* Remove */}
                <button type="button" onClick={() => removeRow(idx)}
                  style={{ width: 24, height: 24, padding: 0, background: "transparent", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", cursor: "pointer", fontSize: 12, lineHeight: 1, flexShrink: 0 }}
                  title="Remove">×</button>

                {/* Note (full width below the row) */}
                {(row.note || row._editingNote) && (
                  <div style={{ flex: "1 1 100%", marginTop: 6 }}>
                    <input type="text" placeholder="Note (optional)" value={row.note || ""}
                      onChange={e => patchRow(idx, { note: e.target.value })}
                      style={{ width: "100%", padding: "5px 8px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", color: "rgba(232,234,246,0.85)", fontFamily: "'Rajdhani', sans-serif", fontSize: 12, borderRadius: 2 }} />
                  </div>
                )}
                {!row.note && !row._editingNote && (
                  <button type="button" onClick={() => patchRow(idx, { _editingNote: true })}
                    style={{ background: "transparent", border: "none", color: "rgba(232,234,246,0.35)", fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, cursor: "pointer", textTransform: "uppercase", flexShrink: 0 }}>
                    + Note
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ItemPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleAdd}
        groupFilter={groupFilter}
        categoryFilter={categoryFilter}
        title={addLabel}
        accentColor={accentColor}
      />
    </div>
  );
}
